import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { DateTimePicker } from "@mui/x-date-pickers"
import { isValid as isDateValid } from "date-fns"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { calendarMeetingErrorMessage } from "./calendarMeetingUtils"

const REASON_MAX_LENGTH = 500
const REASON_MIN_LENGTH = 3

function ReasonField({ label, value, onChange }) {
  const trimmed = value.trim()
  const tooShort = trimmed.length > 0 && trimmed.length < REASON_MIN_LENGTH
  return (
    <TextField
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      multiline
      minRows={3}
      fullWidth
      required
      inputProps={{ maxLength: REASON_MAX_LENGTH }}
      error={tooShort}
      helperText={
        tooShort
          ? `Reason must be at least ${REASON_MIN_LENGTH} characters.`
          : `${trimmed.length}/${REASON_MAX_LENGTH}`
      }
    />
  )
}

ReasonField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}

// Moves the event on the user's Google or Outlook calendar. The candidate gets
// an updated invitation from the calendar.
export function RescheduleCalendarMeetingDialog({
  open,
  onClose,
  meeting,
  onChanged,
}) {
  const [newDatetime, setNewDatetime] = useState(null)
  const [reason, setReason] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const axios = useAuthorizedAxiosClient()

  const startDatetime = meeting?.start_datetime

  useEffect(() => {
    if (!open) return
    setReason("")
    setErrorMessage(null)
    setNewDatetime(startDatetime ? new Date(startDatetime) : new Date())
  }, [open, startDatetime])

  const trimmedReason = reason.trim()
  const isFuture =
    isDateValid(newDatetime) && newDatetime.getTime() > Date.now()
  const canSubmit =
    isFuture && trimmedReason.length >= REASON_MIN_LENGTH && !isSubmitting

  function handleSubmit() {
    setIsSubmitting(true)
    setErrorMessage(null)
    axios
      .post(`/crm/api/meeting-schedule/${meeting.id}/reschedule/`, {
        new_datetime: newDatetime.toISOString(),
        reason: trimmedReason,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      .then(({ data }) => {
        onChanged(
          data,
          "Meeting rescheduled. The candidate was sent an update."
        )
        onClose()
      })
      .catch((error) =>
        setErrorMessage(
          calendarMeetingErrorMessage(
            error,
            "Could not reschedule this meeting."
          )
        )
      )
      .finally(() => setIsSubmitting(false))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Reschedule meeting</DialogTitle>
      <DialogContent>
        <Column sx={{ gap: 3, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The event moves on your calendar and the candidate receives an
            updated invitation.
          </Typography>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <DateTimePicker
            label="New date & time"
            value={newDatetime}
            onChange={setNewDatetime}
            disablePast
            slotProps={{ textField: { fullWidth: true, required: true } }}
          />
          <ReasonField
            label="Reason for reschedule"
            value={reason}
            onChange={setReason}
          />
        </Column>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
        <LoadingButton
          variant="contained"
          loading={isSubmitting}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          Save new time
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

RescheduleCalendarMeetingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  meeting: PropTypes.shape({
    id: PropTypes.number,
    start_datetime: PropTypes.string,
  }),
  onChanged: PropTypes.func.isRequired,
}

// Cancels the event on the calendar and notifies the candidate.
export function CancelCalendarMeetingDialog({
  open,
  onClose,
  meeting,
  onChanged,
}) {
  const [reason, setReason] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const axios = useAuthorizedAxiosClient()

  useEffect(() => {
    if (!open) return
    setReason("")
    setErrorMessage(null)
  }, [open])

  const trimmedReason = reason.trim()
  const canSubmit = trimmedReason.length >= REASON_MIN_LENGTH && !isSubmitting

  function handleSubmit() {
    setIsSubmitting(true)
    setErrorMessage(null)
    axios
      .post(`/crm/api/meeting-schedule/${meeting.id}/cancel/`, {
        reason: trimmedReason,
      })
      .then(({ data }) => {
        onChanged(data, "Meeting cancelled. The candidate was notified.")
        onClose()
      })
      .catch((error) =>
        setErrorMessage(
          calendarMeetingErrorMessage(error, "Could not cancel this meeting.")
        )
      )
      .finally(() => setIsSubmitting(false))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cancel meeting</DialogTitle>
      <DialogContent>
        <Column sx={{ gap: 3, pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The event is removed from your calendar and the candidate receives a
            cancellation.
          </Typography>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <ReasonField
            label="Reason for cancelling"
            value={reason}
            onChange={setReason}
          />
        </Column>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="inherit">
          Keep meeting
        </Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={isSubmitting}
          disabled={!canSubmit}
          onClick={handleSubmit}
        >
          Cancel meeting
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

CancelCalendarMeetingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  meeting: PropTypes.shape({ id: PropTypes.number }),
  onChanged: PropTypes.func.isRequired,
}
